import { useState, useEffect } from 'react'
import { getDashboardOverview, DashboardFilters } from '../services/api'
import MetricCard from '../components/MetricCard'
import LoadingSpinner from '../components/LoadingSpinner'
import FilterBar from '../components/FilterBar'
import { ExportButton } from '../components/ExportButton'
import CostRevenueByLevel from '../components/CostRevenueByLevel'

interface DashboardData {
  summary: {
    totalLeads: number
    totalSpend: number
    totalRevenue: number
    profit: number
  }
  kpis: {
    roi: number
    cpl: number
    rpl: number
    profitMargin: number
    validationRate: number
  }
  channels: {
    google: { spend: number; percentage: number }
    microsoft: { spend: number; percentage: number }
    facebook: { spend: number; percentage: number }
    tiktok: { spend: number; percentage: number }
  }
  costRevenueByLevel: {
    producido: { leads: number; revenue: number; cpl: number; rpl: number }
    unico: { leads: number; revenue: number; cpl: number; rpl: number }
  }
  metadata: {
    dataSource: string
    totalRecords: number
    lastUpdated: string
    currency: string
  }
}

const GeneralOverview = () => {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'all',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async (appliedFilters?: DashboardFilters) => {
    try {
      setLoading(true)
      
      // Usar filtros aplicados o los filtros actuales del estado
      const filtersToUse = appliedFilters || {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }
      
      const response = await getDashboardOverview(filtersToUse)
      setData(response.data)
      setError(null)
      
      console.log('📊 Datos cargados con filtros:', filtersToUse)
    } catch (err) {
      setError('Error al cargar los datos del dashboard')
      console.error('Error fetching dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  // Cargar datos iniciales sin filtros
  useEffect(() => {
    fetchData()
  }, [])

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    console.log('Actualizando filtro de fechas:', { startDate, endDate })
    const newFilters = { ...filters, startDate, endDate }
    setFilters(newFilters)
    
    // Aplicar filtros inmediatamente (solo para fechas)
    const apiFilters: DashboardFilters = {
      startDate,
      endDate,
      source: newFilters.source,
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleSourceChange = (source: string) => {
    console.log('Actualizando filtro de fuente:', source)
    const newFilters = { ...filters, source }
    setFilters(newFilters)
    
    // Aplicar filtros automáticamente
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source,
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleTypeChange = (type: string) => {
    console.log('Actualizando filtro de tipo:', type)
    const newFilters = { ...filters, type }
    setFilters(newFilters)
    
    // Aplicar filtros automáticamente
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: newFilters.source,
      type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  const handleFlowChange = (flow: string) => {
    console.log('Actualizando filtro de flow:', flow)
    const newFilters = { ...filters, flow }
    setFilters(newFilters)
    
    // Aplicar filtros automáticamente
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: newFilters.source,
      type: newFilters.type,
      flow
    }
    fetchData(apiFilters)
  }


  // Función para sincronizar filtros desde FilterBar
  const handleFiltersChange = (newFilters: {
    startDate: string
    endDate: string
    source: string
    type: string
    flow: string
  }) => {
    console.log('Sincronizando filtros:', newFilters)
    setFilters(newFilters)
    
    // Aplicar filtros automáticamente cuando cambien
    const apiFilters: DashboardFilters = {
      startDate: newFilters.startDate,
      endDate: newFilters.endDate,
      source: newFilters.source,
      type: newFilters.type,
      flow: newFilters.flow
    }
    fetchData(apiFilters)
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">📊</span>
            General Overview
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis completo del rendimiento de campañas y flujos de leads
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ExportButton 
            type="overview"
            filters={filters}
          />
          <div className="text-right">
            <div className="text-sm text-dashboard-muted">
              Última actualización
            </div>
            <div className="text-sm font-semibold text-dashboard-text">
              {new Date(data.metadata.lastUpdated).toLocaleString('es-ES')}
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        onDateRangeChange={handleDateRangeChange}
        onSourceChange={handleSourceChange}
        onTypeChange={handleTypeChange}
        onFlowChange={handleFlowChange}
        onFiltersChange={handleFiltersChange}
        loading={loading}
        initialStartDate={filters.startDate}
        initialEndDate={filters.endDate}
        initialSource={filters.source}
        initialType={filters.type}
        initialFlow={filters.flow}
      />

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Leads"
          value={data.summary.totalLeads.toLocaleString()}
          icon="👥"
          subtitle="Leads generados"
          change="Último período"
        />
        <MetricCard
          title="Inversión Total"
          value={`€${data.summary.totalSpend.toFixed(2)}`}
          icon="💰"
          subtitle="Gasto en publicidad"
          premium={true}
        />
        <MetricCard
          title="Revenue"
          value={`€${data.summary.totalRevenue.toFixed(2)}`}
          icon="💎"
          subtitle="Ingresos generados"
          changePercent={12.5}
        />
        <MetricCard
          title="Profit/Loss"
          value={`€${data.summary.profit.toFixed(2)}`}
          icon={data.summary.profit >= 0 ? "📈" : "📉"}
          trend={data.summary.profit >= 0 ? "positive" : "negative"}
          subtitle="Beneficio neto"
          changePercent={data.summary.profit >= 0 ? 5.2 : -73.1}
        />
      </div>

      {/* KPIs Avanzados */}
      <div>
        <h2 className="text-xl font-bold text-dashboard-text mb-4 flex items-center">
          <span className="mr-2">⚡</span>
          Indicadores Clave de Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard
            title="ROI"
            value={`${data.kpis.roi.toFixed(1)}%`}
            icon="📊"
            trend={data.kpis.roi >= 0 ? "positive" : "negative"}
            subtitle="Return on Investment"
            premium={data.kpis.roi >= 0}
          />
          <MetricCard
            title="CPL"
            value={`€${data.kpis.cpl.toFixed(3)}`}
            icon="🎯"
            subtitle="Cost Per Lead"
          />
          <MetricCard
            title="RPL"
            value={`€${data.kpis.rpl.toFixed(3)}`}
            icon="💎"
            subtitle="Revenue Per Lead"
          />
          <MetricCard
            title="Profit Margin"
            value={`${data.kpis.profitMargin.toFixed(1)}%`}
            icon="📈"
            trend={data.kpis.profitMargin >= 0 ? "positive" : "negative"}
            subtitle="Margen de beneficio"
          />
          <MetricCard
            title="Validation Rate"
            value={`${(data.kpis.validationRate * 100).toFixed(1)}%`}
            icon="✅"
            subtitle="Tasa de validación"
            changePercent={2.3}
          />
        </div>
      </div>

      {/* Traffic Source Performance */}
      <div>
        <h2 className="text-xl font-bold text-dashboard-text mb-4 flex items-center">
          <span className="mr-2">🚀</span>
          Traffic Source Performance
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(data.channels).map(([channel, channelData]) => {
            const channelConfig = {
              google: { icon: '🔍', name: 'Google Ads', color: 'bg-blue-500' },
              microsoft: { icon: '🟦', name: 'Microsoft Ads', color: 'bg-orange-500' },
              facebook: { icon: '📘', name: 'Facebook Ads', color: 'bg-blue-600' },
              tiktok: { icon: '🎵', name: 'TikTok Ads', color: 'bg-black' }
            }[channel] || { icon: '📊', name: channel, color: 'bg-gray-500' }

            return (
              <div key={channel} className="card p-6 hover:shadow-card-hover transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 ${channelConfig.color} rounded-xl flex items-center justify-center text-white text-lg`}>
                      {channelConfig.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-dashboard-text">
                        {channelConfig.name}
                      </h3>
                      <p className="text-xs text-dashboard-muted">
                        {channelData.percentage.toFixed(1)}% del total
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-2xl font-bold text-dashboard-text">
                      €{channelData.spend.toFixed(2)}
                    </p>
                    <p className="text-sm text-dashboard-muted">Inversión total</p>
                  </div>
                  
                  <div className="w-full bg-dashboard-border-light rounded-full h-2">
                    <div 
                      className={`${channelConfig.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${channelData.percentage}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-dashboard-muted">
                    <span>Performance</span>
                    <span className={channelData.percentage > 25 ? 'text-dashboard-success' : 'text-dashboard-warning'}>
                      {channelData.percentage > 25 ? 'Alto' : 'Medio'}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Coste y Revenue por Nivel (H1 / Fase A #3) */}
      <CostRevenueByLevel data={data.costRevenueByLevel} />

      {/* Dataset Information */}
      <div className="card p-6 bg-gradient-to-r from-dashboard-bg-dark to-white">
        <h2 className="text-lg font-bold text-dashboard-text mb-6 flex items-center">
          <span className="mr-2">📋</span>
          Información del Dataset
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-globo-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">📊</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Fuente de Datos</p>
            <p className="font-semibold text-dashboard-text">{data.metadata.dataSource}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">🗂️</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Total Registros</p>
            <p className="font-semibold text-dashboard-text">{data.metadata.totalRecords.toLocaleString()}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">💱</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Moneda</p>
            <p className="font-semibold text-dashboard-text">{data.metadata.currency}</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <span className="text-xl">⚡</span>
            </div>
            <p className="text-sm text-dashboard-muted mb-1">Estado</p>
            <p className="font-semibold text-dashboard-text">Actualización Manual</p>
          </div>
        </div>
        
        <div className="mt-6 pt-6 border-t border-dashboard-border-light">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-dashboard-muted">
              <span>📅</span>
              <span>Datos actualizados manualmente desde Excel</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-dashboard-text">
                {new Date().toLocaleDateString('es-ES')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GeneralOverview
